/**
 * Field-Add Submissions (PR5)
 * ───────────────────────────
 * Append-only ledger of contractor / crew field-add requests against a
 * specific job. Mirrors the laborActualRecords pattern (PR4b):
 *
 *   • New submission → append `{ status: "pending", supersedesId: null }`
 *   • Approval/rejection → append a NEW row pointing at the previous via
 *     `supersedesId`. Original row is preserved untouched (audit trail).
 *
 * Resolver consumers (`getJobPayRun`) only see the EFFECTIVE record per
 * submission chain — superseded rows are filtered out.
 *
 * Tier-handling lives in the pay-run resolver (`categorizeFieldAdd`):
 *   • Amount ≤ payProfile.fieldAddCapAmount   → ops_within_cap   (no block)
 *   • Amount  > payProfile.fieldAddCapAmount  → accounting_above_cap (blocks
 *                                               release until status=approved)
 *
 * This module is intentionally identity-agnostic: it does NOT validate
 * who can approve. Route gating happens in the page (ALLOWED_ROLES).
 */
import type { FieldAddInput, FieldAddStatus } from "@/data/finance/payRun/getJobPayRun";

export interface FieldAddSubmission {
  id: string;
  jobId: string;
  /** Contractor/party the field-add accrues to. Null for internal-crew. */
  partyId: string | null;
  amount: number;
  reason: string;
  status: FieldAddStatus;
  createdAt: string;
  createdBy: string;
  decidedAt?: string;
  decidedBy?: string;
  /** Points at the prior submission row this entry supersedes. */
  supersedesId: string | null;
}

export interface SubmitFieldAddInput {
  jobId: string;
  partyId: string | null;
  amount: number;
  reason: string;
  submittedBy: string;
}

export function createFieldAddSubmission(
  input: SubmitFieldAddInput,
): FieldAddSubmission {
  if (!Number.isFinite(input.amount) || input.amount <= 0) {
    throw new Error("Field-add amount must be a positive number");
  }
  if (!input.reason || input.reason.trim().length < 4) {
    throw new Error("Field-add reason is required (≥4 chars)");
  }
  return {
    id: `fa-${input.jobId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    jobId: input.jobId,
    partyId: input.partyId,
    amount: Math.round(input.amount * 100) / 100,
    reason: input.reason.trim(),
    status: "pending",
    createdAt: new Date().toISOString(),
    createdBy: input.submittedBy,
    supersedesId: null,
  };
}

export interface DecideFieldAddInput {
  /** The submission chain to act on (any row in the chain works). */
  submission: FieldAddSubmission;
  decision: Extract<FieldAddStatus, "approved" | "rejected">;
  decidedBy: string;
  /** Optional additional note appended to the reason for audit. */
  note?: string;
}

export function decideFieldAddSubmission(
  input: DecideFieldAddInput,
): FieldAddSubmission {
  if (input.submission.status !== "pending") {
    throw new Error(`Field-add ${input.submission.id} is already ${input.submission.status}`);
  }
  return {
    ...input.submission,
    id: `fa-${input.submission.jobId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    status: input.decision,
    decidedAt: new Date().toISOString(),
    decidedBy: input.decidedBy,
    supersedesId: input.submission.id,
    reason: input.note ? `${input.submission.reason} · ${input.note}` : input.submission.reason,
  };
}

/**
 * Returns the EFFECTIVE field-add records (latest in each supersede chain)
 * for a given job + party, projected into the shape the pay-run resolver
 * expects.
 */
export function getEffectiveFieldAdds(
  jobId: string,
  partyId: string | null | undefined,
  records: FieldAGGREGATE | FieldAddSubmission[],
): FieldAddInput[] {
  const arr = records as FieldAddSubmission[];
  const superseded = new Set(arr.map((r) => r.supersedesId).filter((x): x is string => !!x));
  return arr
    .filter((r) => r.jobId === jobId)
    .filter((r) => (partyId === undefined ? true : (r.partyId ?? null) === (partyId ?? null)))
    .filter((r) => !superseded.has(r.id))
    .map<FieldAddInput>((r) => ({
      id: r.id,
      amount: r.amount,
      status: r.status,
      reason: r.reason,
      approvedBy: r.decidedBy,
    }));
}

// Internal alias so the public signature reads cleanly while still
// supporting an arbitrary readonly array.
type FieldAGGREGATE = ReadonlyArray<FieldAddSubmission>;

/** Pending submissions across the whole platform, oldest first. */
export function getPendingFieldAdds(
  records: ReadonlyArray<FieldAddSubmission>,
): FieldAddSubmission[] {
  const superseded = new Set(records.map((r) => r.supersedesId).filter((x): x is string => !!x));
  return records
    .filter((r) => r.status === "pending" && !superseded.has(r.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
